import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Download, Send, Trash2, Search, Plus, Edit2 } from "lucide-react";

export default function AdminInvoices() {
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newInv, setNewInv] = useState({ user_id: "", plan_name: "", amount_excl_vat: 0, vat_pct: 21, status: "open", description: "" });

  const fetchData = () => {
    Promise.all([
      api.get("/admin/invoices/all"),
      api.get("/admin/customers"),
    ]).then(([i, c]) => {
      setInvoices(i.data);
      setCustomers(c.data);
      setSelected(new Set());
    }).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const downloadPdf = async (inv) => {
    try {
      const response = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `${inv.invoice_number}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("PDF gedownload");
    } catch { toast.error("PDF niet beschikbaar"); }
  };

  const resendInvoice = async (id) => {
    try {
      const res = await api.post(`/admin/invoices/${id}/resend`);
      toast.success(res.data.message);
    } catch { toast.error("Fout bij verzenden"); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Factuur definitief verwijderen?")) return;
    await api.delete(`/admin/invoices/${id}`);
    toast.success("Factuur verwijderd");
    fetchData();
  };

  const bulkDelete = async () => {
    if (!window.confirm(`${selected.size} facturen verwijderen?`)) return;
    await api.post("/admin/invoices/bulk-delete", { ids: [...selected] });
    toast.success(`${selected.size} facturen verwijderd`);
    fetchData();
  };

  const handleCreate = async () => {
    try {
      await api.post("/admin/invoices", newInv);
      toast.success("Factuur aangemaakt");
      setShowCreate(false);
      setNewInv({ user_id: "", plan_name: "", amount_excl_vat: 0, vat_pct: 21, status: "open", description: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Fout bij aanmaken"); }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/admin/invoices/${editing.id}`, {
        plan_name: editing.plan_name,
        amount_excl_vat: parseFloat(editing.amount_excl_vat),
        vat_pct: parseFloat(editing.vat_pct),
        status: editing.status,
        description: editing.description,
      });
      toast.success("Factuur bijgewerkt");
      setEditing(null);
      fetchData();
    } catch { toast.error("Fout bij opslaan"); }
  };

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((i) => i.id)));

  const filtered = invoices.filter((i) =>
    (i.invoice_number || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.user?.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.user?.name || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Facturen Beheer</h1>
          <p className="text-sm text-muted-foreground mt-1">Beheer alle facturen, maak nieuwe aan of bewerk bestaande.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{invoices.length} facturen</Badge>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-invoice-btn">
                <Plus className="w-4 h-4 mr-2" />Factuur Aanmaken
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nieuwe Factuur Aanmaken</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label>Klant *</Label>
                  <Select value={newInv.user_id} onValueChange={(v) => setNewInv({...newInv, user_id: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecteer klant..." /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Omschrijving / Plan</Label><Input value={newInv.plan_name} onChange={(e) => setNewInv({...newInv, plan_name: e.target.value})} placeholder="Bijv. Extra Small (Monthly)" className="mt-1" /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label>Bedrag excl. BTW (EUR)</Label><Input type="number" step="0.01" value={newInv.amount_excl_vat} onChange={(e) => setNewInv({...newInv, amount_excl_vat: e.target.value})} className="mt-1" /></div>
                  <div><Label>BTW %</Label><Input type="number" step="0.1" value={newInv.vat_pct} onChange={(e) => setNewInv({...newInv, vat_pct: e.target.value})} className="mt-1" /></div>
                  <div>
                    <Label>Status</Label>
                    <Select value={newInv.status} onValueChange={(v) => setNewInv({...newInv, status: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="paid">Betaald</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notitie (optioneel)</Label><Input value={newInv.description} onChange={(e) => setNewInv({...newInv, description: e.target.value})} placeholder="Interne notitie..." className="mt-1" /></div>
                <div className="bg-accent/30 rounded-lg p-3 text-sm">
                  <p>Berekend totaal: <strong className="font-mono-data">{(parseFloat(newInv.amount_excl_vat || 0) * (1 + parseFloat(newInv.vat_pct || 0) / 100)).toFixed(2)} EUR</strong> incl. BTW</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Annuleren</Button>
                <Button onClick={handleCreate} className="bg-blue-600" disabled={!newInv.user_id} data-testid="create-invoice-btn">Factuur Aanmaken</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op factuurnummer, naam of email..." className="pl-9" />
        </div>
        {selected.size > 0 && (
          <Button variant="destructive" size="sm" onClick={bulkDelete} data-testid="bulk-delete-invoices">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />{selected.size} verwijderen
          </Button>
        )}
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} /></TableHead>
              <TableHead>Nummer</TableHead>
              <TableHead>Klant</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead className="text-right">Excl. BTW</TableHead>
              <TableHead className="text-right">Totaal</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Datum</TableHead>
              <TableHead className="text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Geen facturen gevonden</TableCell></TableRow>
            ) : filtered.map((inv) => (
              <TableRow key={inv.id} className={`table-row-hover ${selected.has(inv.id) ? "bg-primary/5" : ""}`}>
                <TableCell><Checkbox checked={selected.has(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} /></TableCell>
                <TableCell className="font-mono-data text-sm font-medium">{inv.invoice_number}</TableCell>
                <TableCell>
                  <div className="text-sm">{inv.user?.name || "-"}</div>
                  <div className="text-xs text-muted-foreground">{inv.user?.email || "-"}</div>
                </TableCell>
                <TableCell>{inv.plan_name} {inv.frequency && inv.frequency !== "-" && <span className="text-xs text-muted-foreground capitalize">({inv.frequency})</span>}</TableCell>
                <TableCell className="text-right font-mono-data">{inv.amount_excl_vat?.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono-data font-medium">{inv.total?.toFixed(2)} EUR</TableCell>
                <TableCell>
                  <Badge className={inv.status === "paid" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{inv.status === "paid" ? "Betaald" : "Open"}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{inv.created_at ? new Date(inv.created_at).toLocaleDateString("nl-NL") : ""}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({...inv})} title="Bewerken"><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadPdf(inv)} title="Download PDF"><Download className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#2CA8AC]" onClick={() => resendInvoice(inv.id)} title="Opnieuw verzenden"><Send className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteInvoice(inv.id)} title="Verwijderen"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Edit Invoice Dialog */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Factuur Bewerken: {editing.invoice_number}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-accent/30 rounded-lg p-3 text-sm">
                <p><strong>Klant:</strong> {editing.user?.name} ({editing.user?.email})</p>
                <p><strong>Factuurnummer:</strong> {editing.invoice_number}</p>
              </div>
              <div><Label>Omschrijving / Plan</Label><Input value={editing.plan_name || ""} onChange={(e) => setEditing({...editing, plan_name: e.target.value})} className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Bedrag excl. BTW (EUR)</Label><Input type="number" step="0.01" value={editing.amount_excl_vat || 0} onChange={(e) => setEditing({...editing, amount_excl_vat: e.target.value})} className="mt-1" /></div>
                <div><Label>BTW %</Label><Input type="number" step="0.1" value={editing.vat_pct || 21} onChange={(e) => setEditing({...editing, vat_pct: e.target.value})} className="mt-1" /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({...editing, status: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="paid">Betaald</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notitie</Label><Input value={editing.description || ""} onChange={(e) => setEditing({...editing, description: e.target.value})} className="mt-1" /></div>
              <div className="bg-accent/30 rounded-lg p-3 text-sm">
                <p>Berekend totaal: <strong className="font-mono-data">{(parseFloat(editing.amount_excl_vat || 0) * (1 + parseFloat(editing.vat_pct || 21) / 100)).toFixed(2)} EUR</strong> incl. BTW</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button>
              <Button onClick={handleUpdate} className="bg-blue-600" data-testid="save-invoice-btn">Opslaan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
